/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { IMessage } from '../../interfaces/component-interfaces';
import { STORAGE_KEYS } from '../../config/miaw/constants';
import { IParticipantChangedPayload, IRoutingResultPayload, IStaticMessageConversationPayload } from '../../interfaces/agent-action-interfaces';
import { IApiHooksWithTrigger, IIdentityObject } from '../../interfaces/app-interfaces';
import { getContinuityJwt, listConversationEntries, listConversations } from '../../utils/miaw/api-utils';
import { setJwt, storeConversationId } from '../../utils/miaw/chat-config';
import { setItemInWebStorage } from '../../utils/miaw/web-storage-utils';
import { getConversationEventMessage } from '../../utils/component-utils';
import { useNRLogging } from '../custom-hooks/useNRLogging';
import { useMIAWAgentActions } from '../miaw/useMIAWAgentActions';
import { handleLink } from '../../utils/miaw/agent-messages-utils';
import { setIsUserLoggedInAtChatStart } from '../../utils/session-storage-utils';
import { useIdentity } from '@vp/auth-react';
import { MiawChatContext } from '../../contexts/MiawChatContext';
import { useChatFooterWebSocket } from '../../hooks/chatbot/useChatFooterWebSocket';
import ResumeButton from '../../components/AdvanceMessagingComponent/ResumeButton/ResumeButton';

const useMIAWChatRetrieve = (): IApiHooksWithTrigger<{ messages: IMessage[]; conversationId: string; agentName: string }> => {
  const {
    state: { sfConfig, conversationId },
  } = useContext(MiawChatContext);

  const identity = useIdentity();

  const { logCustomAttributesNR } = useNRLogging();

  const { subscribeToAgentActions } = useMIAWAgentActions();

  const {
    state: { enableSneakPeak },
    setState,
  } = useContext(MiawChatContext);

  const [data, setData] = useState<{ messages: IMessage[]; conversationId: string; agentName: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [toggle, setToggle] = useState<boolean>(false);

  const webSocket = useChatFooterWebSocket({ conversationId: conversationId || '', isrequestConnection: false });
  const closeConnection = webSocket?.closeConnection;

  const trigger = useCallback(() => {
    setToggle((prev) => !prev);
  }, []);

  useEffect(() => {
    if (toggle) {
      setLoading(true);
      try {
        logCustomAttributesNR('ca-chatbot-miaw-api-request', {
          logLevel: 'info',
          message: 'Retrieving conversation',
          apiType: 'miawRetrieveConversation',
        });
        getContinuityJwt(sfConfig)
          .then((response) => {
            setJwt(response.accessToken);
            setItemInWebStorage(STORAGE_KEYS.JWT, response.accessToken);
          })
          .then(() => {
            listConversations(sfConfig)
              .then((response) => {
                if (response && response.openConversationsFound > 0 && response.conversations.length) {
                  const openConversations = response.conversations;
                  if (openConversations.length > 1) {
                    console.warn(
                      `Expected the user to be participating in 1 open conversation but instead found ${openConversations.length}. Loading the conversation with latest startTimestamp.`,
                    );
                    openConversations.sort((conversationA: any, conversationB: any) => conversationB.startTimestamp - conversationA.startTimestamp);
                  }
                  // Update conversation-id with the one from service.
                  storeConversationId(openConversations[0].conversationId);
                  //   setState((prev) => ({
                  //     ...prev,
                  //     conversationStatus: CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION,
                  //     conversationId: openConversations[0].conversationId
                  //   }));
                  listConversationEntries(openConversations[0].conversationId, sfConfig).then((response) => {
                    const conversationEntries: IMessage[] = [];
                    let agentName = '';
                    let parsedPayload, messageContent;
                    response.reverse().forEach((item, index) => {
                      const entry = item.conversationEntry;
                      switch (entry.entryType) {
                        case 'Message': {
                          parsedPayload = entry.entryPayload as IStaticMessageConversationPayload;
                          let urlMapping: { label: string; url: string } | undefined = undefined;

                          if (parsedPayload.abstractMessage.staticContent.formatType === 'RichLink') {
                            const linkData = handleLink(parsedPayload);
                            if (linkData) urlMapping = linkData;
                          }
                          const messageList: string[] = [];
                          if (parsedPayload.abstractMessage.staticContent.text) {
                            messageList.push(parsedPayload.abstractMessage.staticContent.text);
                          }
                          if (urlMapping?.url) {
                            messageList.push(urlMapping.url);
                          }
                          conversationEntries.push({
                            id: entry.identifier,
                            messageContent: messageList.length > 0 ? messageList : [''],
                            senderDisplayName: entry.senderDisplayName,
                            timestamp: entry.clientTimestamp,
                            type: entry.sender.role === 'EndUser' ? 'user' : 'agent',
                            showFeedback: false,
                            formatType: parsedPayload?.abstractMessage?.staticContent?.formatType,
                            attachments:
                              parsedPayload?.abstractMessage?.staticContent?.formatType === 'Attachments'
                                ? parsedPayload?.abstractMessage?.staticContent?.attachments
                                : undefined,
                            urlMapping: [...(urlMapping ? [urlMapping] : [])],
                          });
                          break;
                        }
                        case 'RoutingResult':
                          parsedPayload = entry.entryPayload as IRoutingResultPayload;
                          if (parsedPayload?.failureType === 'RoutingError' && parsedPayload?.failureReason && parsedPayload?.failureReason !== '') {
                            logCustomAttributesNR('ca-chatbot-miaw-retrieve-chat', {
                              logLevel: 'error',
                              message: 'Agents were not available',
                              conversationId: openConversations[0].conversationId,
                              apiType: 'miawRetrieveConversation',
                            });
                            conversationEntries.push({
                              id: entry.identifier,
                              messageContent:
                                parsedPayload?.failureReason ??
                                'Sorry, our specialists aren’t available right now. Please try again later or contact us by email or phone instead.',
                              senderDisplayName: entry.senderDisplayName,
                              timestamp: entry.clientTimestamp,
                              type: 'system',
                              showFeedback: false,
                            });

                            setState((prev) => ({ ...prev, isRoutingError: true }));
                            break;
                          }
                          conversationEntries.push({
                            id: entry.identifier,
                            messageContent: `${enableSneakPeak ? "The specialist can see what you're typing to help you faster." : 'A specialist will be with you soon'}`,
                            // messageContent: 'The specialist can see what you're typing to help you faster.',
                            senderDisplayName: entry.senderDisplayName,
                            timestamp: entry.clientTimestamp,
                            hideTimestamp: enableSneakPeak,
                            type: 'system',
                            showFeedback: false,
                            formatType: parsedPayload?.abstractMessage?.staticContent?.formatType,
                            attachments:
                              parsedPayload?.abstractMessage?.staticContent?.formatType === 'Attachments'
                                ? parsedPayload?.abstractMessage?.staticContent?.attachments
                                : undefined,
                          });
                          break;
                        case 'ParticipantChanged':
                          parsedPayload = entry.entryPayload as IParticipantChangedPayload;
                          messageContent = parsedPayload.entries?.[0]
                            ? getConversationEventMessage(parsedPayload.entries[0].operation, parsedPayload.entries[0].displayName)
                            : 'Participant changed';
                          conversationEntries.push({
                            id: entry.identifier,
                            messageContent,
                            senderDisplayName: entry.senderDisplayName,
                            timestamp: entry.clientTimestamp,
                            type: 'system',
                            showFeedback: false,
                            formatType: parsedPayload?.abstractMessage?.staticContent?.formatType,
                            attachments:
                              parsedPayload?.abstractMessage?.staticContent?.formatType === 'Attachments'
                                ? parsedPayload?.abstractMessage?.staticContent?.attachments
                                : undefined,
                          });
                          if (
                            index == response.length - 1 &&
                            parsedPayload.entries?.[0]?.participant?.role == 'Agent' &&
                            parsedPayload.entries?.[0]?.operation === 'remove'
                          ) {
                            conversationEntries.push({
                              id: entry.identifier,
                              messageContent: <ResumeButton />,
                              timestamp: entry.clientTimestamp,
                              type: 'Resume',
                              showFeedback: false,
                            });
                            setState((prev) => ({ ...prev, isChatEndedByAgent: true }));
                          }
                          if (parsedPayload.entries?.[0]?.operation === 'add') agentName = parsedPayload.entries[0].displayName;
                          break;
                        default:
                          console.log(entry);
                      }
                    });
                    const { isSignedIn } = identity as IIdentityObject;
                    setIsUserLoggedInAtChatStart(isSignedIn);

                    setData({ messages: conversationEntries, conversationId: openConversations[0].conversationId, agentName });
                    setError(undefined);
                    subscribeToAgentActions();
                    logCustomAttributesNR('ca-chatbot-miaw-api-response', {
                      logLevel: 'info',
                      message: 'Conversation retrieved successfully',
                      conversationId: openConversations[0].conversationId,
                      apiType: 'miawRetrieveConversation',
                    });
                  });
                  // props.showMessagingWindow(true);
                } else {
                  // No open conversations found.
                  // cleanupMessagingData();
                  // props.showMessagingWindow(false);
                }
              })
              .catch((error) => {
                setError(error as Error);
                console.error('Error in retrieving conversation', error);
                logCustomAttributesNR('ca-chatbot-miaw-api-error', {
                  errorName: error.name,
                  errorStack: error.stack,
                  message: error.message,
                  logLevel: 'error',
                  apiType: 'miawRetrieveConversation',
                });
              });
          })
          .catch((error) => {
            closeConnection(error?.message || 'Error in getting continuity JWT');
            setError(error as Error);
            console.error('Error in retrieving conversation', error);
            logCustomAttributesNR('ca-chatbot-miaw-api-error', {
              errorName: error.name,
              errorStack: error.stack,
              message: error.message,
              logLevel: 'error',
              apiType: 'miawRetrieveConversation',
            });
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error) {
        closeConnection(error.message || 'Error in retrieving conversation');
        console.error('Error in retrieving conversation', error);
        setError(error as Error);
        logCustomAttributesNR('ca-chatbot-miaw-api-error', {
          errorName: error.name,
          errorStack: error.stack,
          message: error.message,
          logLevel: 'error',
          apiType: 'miawRetrieveConversation',
        });
      } finally {
        setToggle(false);
        setLoading(false);
      }
    }
  }, [toggle]);

  return {
    loading,
    trigger,
    data,
    error,
  };
};

export { useMIAWChatRetrieve };
